import React, { memo, useEffect, useMemo, useState } from 'react';
import { DeleteConfirmModal } from '../../../../components/DeleteConfirmModal';
import FileSelect from '../../../../components/FileSelect';
import {
  useClearSheetSourceFunds,
  useGetFundMappings,
  useMarkAsPerInputFund,
  useUnmarkAsPerInputFund
} from '../../../../services/funds.service';
import useFundMapping from '../../../../hooks/useFundMapping';
import SourceFundItem from './SourceFundItem';
import { MdRefresh } from 'react-icons/md';
import { PiEraser } from 'react-icons/pi';
import { InfoIcon, warningImage } from './icons';
import { IoArrowForward } from 'react-icons/io5';
import { MdDragIndicator } from 'react-icons/md';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  ScrollArea,
  Text,
  Tooltip
} from '@mantine/core';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { createStyles } from '@mantine/emotion';
import agentStore from '../../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../../components/ChatWidget/constants';
import { useDidUpdate } from '@mantine/hooks';
import { PermissionGuard, DENY_REASONS } from '../../../../components/PermissionGuard';
import { idCreator } from '../../../Project/utils/idCreator';

const getListStyle = isDraggingOver => ({
  background: '#fff',
  padding: 0
});

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  //margin: `0 0 ${grid}px 0`,
  padding: 0,
  // change background colour if dragging
  background: '#fff',

  ...draggableStyle,
  width: isDragging ? '300px' : '100%'
});

const useStyles = createStyles((theme, { isDraging }) => ({
  drawer: {
    '.mantine-Drawer-content': {
      transform: isDraging && 'none!important',
      overflow: 'hidden'
    }
  }
}));

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const isLikelyFundOption = fund => {
  const name = typeof fund?.name === 'string' ? fund.name.trim() : '';
  if (!name || name.length < 3 || name.length > 120) return false;

  const letterCount = (name.match(/[A-Za-z]/g) || []).length;
  const digitCount = (name.match(/\d/g) || []).length;
  if (letterCount < 2 || digitCount > letterCount) return false;

  return !/(fund inception|1-year|3-year|5-year|7-year|10-year|benchmark|annualized|return)/i.test(
    name
  );
};

const FundMappingModal = ({
  isOpen,
  onClose,
  periodId,
  sheetId,
  fileName,
  sheetName,
  nextToExtract,
  fundMappingAction,
  isPerFund,
  refetchHeaderFooter,
  isLockedPeriod,
  isLookup
}) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [draging, setDraging] = useState(false);
  const [draggingElement, setDraggingElement] = useState();
  const [isLoadingSwap, setIsLoadingSwap] = useState();
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isOneFund, setIsOpenFund] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [initiallyAutoMatched, setInitiallyAutoMatched] = useState(false);

  const { classes } = useStyles({ isDraging: draging });

  // Extracted event handlers
  const handleConfirmModalClose = () => {
    setIsConfirmModalOpen(false);
  };

  const handleExtractWithoutMatching = () => {
    nextToExtract();
    handleClose();
  };

  const handleSaveMapping = () => {
    updateAllFundsMapping(() => {
      onClose();
      refetchHeaderFooter();
    });
  };

  const handleSourceFundSelect = idx => val => {
    onSelectSourceFund(val, idx);
  };

  const handleDraggingElementSelect = sourceId => () => {
    onSelectOnDrag(sourceId);
  };

  useEffect(() => {
    setIsOpenFund(isPerFund);
  }, [isPerFund]);

  function openPopup(msg = 'You have unsaved data. Are you sure you want to proceed?') {
    return new Promise((resolve, reject) => {
      // Simulate opening popup
      setTimeout(() => {
        const userResponse = confirm(msg);

        if (userResponse) {
          setHasChanges(false);
        }

        resolve(userResponse);
      }, 100);
    });
  }

  const {
    data: fundMappings,
    refetch,
    isLoading,
    isFetching
  } = useGetFundMappings({
    params: {
      period_id: periodId,
      sheet_id: sheetId
    },
    queryParams: {
      enabled: !!isOpen && !!periodId && !!sheetId,
      onSuccess: () => {
        setHasChanges(false);
        setDraggingElement(false);
      }
    }
  });

  const {
    mappings,
    isDeleteLoading,
    isUpdateLoading,
    isCreateLoading,
    isReExtracting,
    isUpdateListLoading,
    setMappings,
    onReExtractFunds,
    onSelectSourceFund,
    updateAllFundsMapping,
    handleCreateSourceFund,
    handleUpdateSourceFund,
    handleDeleteSourceFund,
    checkFundIsAlreadyChosen,
    notMappedFunds,
    autoNotMappedSourceFunds,
    isLoadingAutoMatch,
    handleClearFunds
  } = useFundMapping({
    refetch,
    setHasChanges,
    sheetId,
    enabled: !!isOpen,
    fund_mappings: fundMappings?.fund_mappings,
    notMappedSourceFunds: fundMappings?.not_mapped_source_funds,
    isOneFund
  });

  const disabledIndividualBtn = useMemo(() => {
    const mappedFundsLength = mappings?.inputs?.filter(
      el => el.source_fund && el.input_fund
    ).length;

    return mappedFundsLength > 1 || mappedFundsLength === 0;
  }, [mappings]);

  const notMappedSourceFundsCount = fundMappings?.not_mapped_input_funds?.length;

  const onSelectOnDrag = async sourceId => {
    if (!draggingElement) setDraggingElement(sourceId);
    else if (draggingElement === sourceId) setDraggingElement(null);
    else {
      const firstElementIndex = mappings?.sources?.findIndex(
        el => el?.source_fund?.id === draggingElement
      );
      const secondElementIndex = mappings?.sources?.findIndex(
        el => el?.source_fund?.id === sourceId
      );

      setMappings(prev => {
        const _sources = [...prev?.sources];

        _sources[firstElementIndex] = mappings?.sources[secondElementIndex];
        _sources[secondElementIndex] = mappings?.sources[firstElementIndex];

        return { ...prev, sources: [..._sources] };
      });

      setHasChanges(true);
      setDraggingElement(null);
      setIsLoadingSwap(false);
    }
  };

  const handleClose = async () => {
    if (hasChanges) {
      const userResponse = await openPopup();

      if (!userResponse) return;
    }

    setInitiallyAutoMatched(false);
    onClose();
    setMappings(null);
  };

  const disabled = isReExtracting || isCreateLoading || isDeleteLoading || isLoading || isFetching;
  const onDragStart = () => {
    setDraging(true);
  };
  const onDragEnd = result => {
    setDraging(false);
    if (!result.destination) {
      return;
    }

    const _sources = JSON.parse(JSON.stringify([...mappings.sources]));

    const items = reorder(
      mappings.sources.map(value => value.source_fund),
      result.source.index,
      result.destination.index
    );

    setMappings(prev => {
      return {
        ...prev,
        sources: _sources.map((value, index) => ({
          ...value,
          source_fund: items[index]
        }))
      };
    });
  };

  useEffect(() => {
    if (isLoading || !isOpen || initiallyAutoMatched || isLookup) return;
    if (!mappings?.inputs || mappings?.inputs?.length === 0) return;

    if (mappings?.sources?.filter(el => el?.source_fund)?.length > 0) {
      setInitiallyAutoMatched(true);
      return;
    }

    onReExtractFunds();
    setInitiallyAutoMatched(true);
  }, [mappings, isLoading, isOpen, isLookup, initiallyAutoMatched]);

  useDidUpdate(() => {
    if (isOpen) {
      agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.FundMappingDrawer);
    } else {
      agentStore.setCurrentSubScreen('');
    }
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <Box
          id="data-extraction-fund-mapping-root"
          sx={() => ({
            width: '45vw',
            zIndex: 99999,
            boxShadow:
              '0px 17px 17px -7px rgba(0, 0, 0, 0.04), 0px 36px 28px -7px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.05)'
          })}
          bg="white"
          h="100vh"
          pos="fixed"
          p="16px"
          right={0}
          bottom={0}
          top={0}
        >
          <Flex
            id="data-extraction-fund-mapping-header"
            justify="space-between"
            align="center"
            mb="10px"
          >
            <Text id="data-extraction-fund-mapping-title">Match Funds</Text>
            <CloseButton id="data-extraction-fund-mapping-close" onClick={handleClose} />
          </Flex>
          <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
            <Droppable droppableId="droppable">
              {(provided, snapshot) => (
                <Box id="data-extraction-fund-mapping-content">
                  <Flex id="data-extraction-fund-mapping-file-sheet-row" align="center" gap="16px">
                    <Flex id="data-extraction-fund-mapping-file-row" gap="4px" align="center">
                      <Text id="data-extraction-fund-mapping-file-label" fz="14px" c="dark.2">
                        File:{' '}
                      </Text>
                      <Text id="data-extraction-fund-mapping-file-value" fz="14px" c="dark.9">
                        {fileName}
                      </Text>
                    </Flex>

                    <Flex id="data-extraction-fund-mapping-sheet-row" gap="4px" align="center">
                      <Text id="data-extraction-fund-mapping-sheet-label" fz="14px" c="dark.2">
                        Sheet:{' '}
                      </Text>
                      <Text id="data-extraction-fund-mapping-sheet-value" fz="14px" c="dark.9">
                        {sheetName}
                      </Text>
                    </Flex>
                  </Flex>

                  <Alert
                    id="data-extraction-fund-mapping-alert"
                    variant="outline"
                    color="blue"
                    icon={<AiOutlineInfoCircle />}
                    mt="24px"
                    p="12px 16px"
                  >
                    Match the funds you added initially with the funds found in the uploaded file
                  </Alert>

                  <Flex
                    id="data-extraction-fund-mapping-actions-row"
                    justify="space-between"
                    align="center"
                    mt="24px"
                  >
                    <PermissionGuard allowed={!isLockedPeriod} reason={DENY_REASONS.LOCKED_PERIOD}>
                      <Button
                        id="data-extraction-fund-mapping-auto-match"
                        leftSection={<MdRefresh fontSize="16px" />}
                        loading={isLoadingAutoMatch}
                        onClick={onReExtractFunds}
                        size="xs"
                        variant="subtle"
                      >
                        Auto Match
                      </Button>
                    </PermissionGuard>

                    <PermissionGuard allowed={!isLockedPeriod} reason={DENY_REASONS.LOCKED_PERIOD}>
                      <Button
                        id="data-extraction-fund-mapping-clear-all"
                        leftSection={<PiEraser fontSize="16px" />}
                        onClick={handleClearFunds}
                        size="xs"
                        variant="subtle"
                        color="red.6"
                      >
                        Clear All
                      </Button>
                    </PermissionGuard>
                  </Flex>

                  <Box
                    style={{
                      border: '1px solid #F1F3F5',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                    w="100%"
                    mt="8px"
                  >
                    <Flex id="data-extraction-fund-mapping-table-header" bg="gray.0" align="center">
                      <Text
                        id="data-extraction-fund-mapping-added-title"
                        w="50%"
                        p="12px"
                        fz="14px"
                        c="gray.7"
                      >
                        Added Funds
                      </Text>
                      <Text
                        id="data-extraction-fund-mapping-file-title"
                        w="50%"
                        p="12px"
                        fz="14px"
                        c="gray.7"
                      >
                        Funds from file&nbsp;
                        {notMappedSourceFundsCount > 0 && (
                          <Text component="span" c="red.6">
                            (Missing {notMappedSourceFundsCount}&nbsp;
                            {notMappedSourceFundsCount === 1 ? 'Fund' : 'Funds'})
                          </Text>
                        )}
                      </Text>
                    </Flex>
                    <ScrollArea
                      id="data-extraction-fund-mapping-scroll"
                      scrollbarSize={14}
                      scrollHideDelay={500}
                      h="calc(100vh - 320px)"
                    >
                      <Flex id="data-extraction-fund-mapping-scroll-body" p="12px" gap="12px">
                        <Flex
                          id="data-extraction-fund-mapping-inputs-column"
                          direction="column"
                          gap="12px"
                          w="50%"
                        >
                          {mappings?.inputs?.map((fund, idx) => {
                            const fundRowId = `data-extraction-fund-input-${idCreator(fund?.id || String(idx))}`;

                            return (
                              <Tooltip
                                key={idx}
                                id={`${fundRowId}-tooltip`}
                                label={fund?.input_fund?.name || fund?.name}
                                maw="320px"
                                multiline
                              >
                                <Flex
                                  id={`${fundRowId}-row`}
                                  align="center"
                                  justify="space-between"
                                  gap="8px"
                                >
                                  <FileSelect
                                    idPrefix={fundRowId}
                                    hasIcon={false}
                                    disabled={true}
                                    readOnly
                                    selectedFile={{
                                      name: fund?.input_fund?.name || fund?.name,
                                      value: fund?.input_fund?.id || fund?.id
                                    }}
                                  />

                                  <IoArrowForward
                                    id={`${fundRowId}-arrow`}
                                    fontSize="20px"
                                    color="#DEE2E6"
                                  />
                                </Flex>
                              </Tooltip>
                            );
                          })}
                        </Flex>

                        <Flex
                          id="data-extraction-fund-mapping-source-column"
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          style={getListStyle(snapshot.isDraggingOver)}
                          w="50%"
                          direction="column"
                          gap="12px"
                        >
                          {mappings?.sources?.map((source, idx) => {
                            const notMappedSourceFunds = autoNotMappedSourceFunds || [];

                            const _options = [
                              ...notMappedSourceFunds?.filter(
                                fund => !checkFundIsAlreadyChosen(fund.id)
                              ),
                              ...notMappedFunds
                            ]
                              .filter(
                                (obj, index, self) => index === self.findIndex(t => t.id === obj.id)
                              )
                              .filter(isLikelyFundOption);

                            const selectedFile = {
                              name: source?.source_fund?.name || source?.name,
                              value: source?.source_fund?.id,
                              id: source?.id
                            };

                            const draggableId =
                              source?.source_fund?.id || source?.id || source?.input_fund?.id;

                            const sourceRowId = `data-extraction-fund-${idCreator(source?.id || String(idx))}`;

                            return (
                              <Draggable key={draggableId} draggableId={draggableId} index={idx}>
                                {(provided, snapshot) => (
                                  <Tooltip
                                    disabled={!selectedFile?.name}
                                    id={`${sourceRowId}-tooltip`}
                                    label={selectedFile?.name}
                                    maw="320px"
                                    multiline
                                  >
                                    <Flex
                                      id={`${sourceRowId}-row`}
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={getItemStyle(
                                        snapshot.isDragging,
                                        provided.draggableProps.style
                                      )}
                                      align="center"
                                      justify="space-between"
                                      gap="8px"
                                    >
                                      <SourceFundItem
                                        idPrefix={sourceRowId}
                                        source={source}
                                        refetch={refetch}
                                        sheetId={sheetId}
                                        disabled={disabled || isLockedPeriod}
                                        isCreatingNew={isCreatingNew}
                                        isCreateLoading={isCreateLoading}
                                        isDeleteLoading={isDeleteLoading}
                                        isUpdateLoading={isUpdateLoading || isLoadingAutoMatch}
                                        setIsCreatingNew={setIsCreatingNew}
                                        handleCreateSourceFund={handleCreateSourceFund}
                                        handleDeleteSourceFund={handleDeleteSourceFund}
                                        handleUpdateSourceFund={handleUpdateSourceFund}
                                        onSelectSourceFund={handleSourceFundSelect(idx)}
                                        isDragging={!!draggingElement}
                                        isDraggedElement={
                                          source?.source_fund?.id === draggingElement
                                        }
                                        onSelectDraggingElement={handleDraggingElementSelect(
                                          source?.source_fund?.id
                                        )}
                                        options={_options}
                                        selectedFile={selectedFile}
                                      />
                                      {!isLockedPeriod && (
                                        <Flex
                                          id={`${sourceRowId}-drag-handle`}
                                          {...provided.dragHandleProps}
                                        >
                                          <MdDragIndicator
                                            id={`${sourceRowId}-drag-icon`}
                                            fontSize="20px"
                                            color="#DEE2E6"
                                          />
                                        </Flex>
                                      )}
                                    </Flex>
                                  </Tooltip>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </Flex>
                      </Flex>
                    </ScrollArea>
                  </Box>
                </Box>
              )}
            </Droppable>
          </DragDropContext>
          <Flex
            id="data-extraction-fund-mapping-footer"
            align="center"
            justify="space-between"
            mt="20px"
          >
            <Flex id="data-extraction-fund-mapping-footer-left" align="center" gap="18px">
              <Button
                id="data-extraction-fund-mapping-cancel"
                disabled={isReExtracting}
                size="xs"
                onClick={handleClose}
                variant="default"
                color="dark"
              >
                Cancel
              </Button>

              <Flex
                id="data-extraction-fund-mapping-single-fund-toggle"
                onClick={disabledIndividualBtn ? () => {} : () => setIsOpenFund(prev => !prev)}
                align="center"
                gap="8px"
                opacity={disabledIndividualBtn ? '0.4' : 1}
              >
                <Flex
                  id="data-extraction-fund-mapping-single-fund-label-row"
                  align="center"
                  gap="4px"
                >
                  <InfoIcon id="data-extraction-fund-mapping-single-fund-info-icon" />
                  <Text id="data-extraction-fund-mapping-single-fund-text" c="dark.9" fz="14px">
                    This sheet has only one fund
                  </Text>
                </Flex>
                <PermissionGuard allowed={!isLockedPeriod} reason={DENY_REASONS.LOCKED_PERIOD}>
                  <Checkbox
                    id="data-extraction-fund-mapping-single-fund-checkbox"
                    size="xs"
                    onChange={() => {}}
                    checked={disabledIndividualBtn ? false : isOneFund}
                  />
                </PermissionGuard>
              </Flex>
            </Flex>

            <Flex
              id="data-extraction-fund-mapping-footer-actions"
              align="center"
              gap="18px"
              className="buttons"
            >
              {fundMappingAction && (
                <PermissionGuard allowed={!isLockedPeriod} reason={DENY_REASONS.LOCKED_PERIOD}>
                  <Button
                    id="data-extraction-fund-mapping-extract-without-matching"
                    variant="subtle"
                    size="xs"
                    onClick={handleExtractWithoutMatching}
                    isLoading={isLoading}
                  >
                    Extract Without Matching
                  </Button>
                </PermissionGuard>
              )}

              <PermissionGuard allowed={!isLockedPeriod} reason={DENY_REASONS.LOCKED_PERIOD}>
                <Button
                  id="data-extraction-fund-mapping-save"
                  size="xs"
                  loading={isUpdateListLoading}
                  disabled={isLoading || isReExtracting}
                  onClick={handleSaveMapping}
                >
                  Save
                </Button>
              </PermissionGuard>
            </Flex>
          </Flex>
        </Box>
      )}
      <DeleteConfirmModal
        id="data-extraction-fund-mapping-confirm-modal"
        isOpen={isConfirmModalOpen}
        image={warningImage}
        onClose={handleConfirmModalClose}
        onConfirm={onClose}
        title="You have unsaved data. Are you sure want to proceed?"
      />
    </>
  );
};

export default memo(FundMappingModal);
